import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, Nav, Form, FormControl, Button, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { LanguageContext } from '../context/LanguageContext';
import { useQuery } from '@tanstack/react-query';
import { fetchLocations } from '../utils/api';
import { buildLocationPath, buildCityPath } from '../utils/urls';

const Header = () => {
    const { t } = useTranslation();
    useContext(LanguageContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    const navigate = useNavigate();
    const suggestionsRef = useRef(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    // Fetch all locations for autocomplete (cached via react-query)
    const { data: locationsData } = useQuery({
        queryKey: ['locations-autocomplete'],
        queryFn: () => fetchLocations({ limit: 200 }),
        staleTime: 1000 * 60 * 30,
        gcTime: 1000 * 60 * 60
    });

    const allLocations = useMemo(() => locationsData?.locations || [], [locationsData]);

    const buildSuggestions = useCallback((term) => {
        if (!term || term.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const lower = term.toLowerCase();
        const results = [];
        const seenCities = new Set();

        for (const loc of allLocations) {
            if (results.length >= 8) break;

            // Match by beekeeper name or title
            const name = `${loc.beekeeper?.firstName || ''} ${loc.beekeeper?.lastName || ''}`.trim();
            const title = loc.title || '';
            if (title.toLowerCase().includes(lower) || name.toLowerCase().includes(lower)) {
                results.push({
                    type: 'imker',
                    label: title || name,
                    sublabel: `${loc.city || ''}, ${loc.state || ''}`,
                    link: buildLocationPath(loc)
                });
            }

            // Match by city
            if (loc.city && loc.city.toLowerCase().includes(lower) && !seenCities.has(loc.city.toLowerCase())) {
                seenCities.add(loc.city.toLowerCase());
                results.push({
                    type: 'city',
                    label: loc.city,
                    sublabel: `${loc.state || ''}, ${loc.countryName || ''}`,
                    link: buildCityPath(loc)
                });
            }
        }

        setSuggestions(results.slice(0, 8));
        setShowSuggestions(results.length > 0);
        setActiveSuggestion(-1);
    }, [allLocations]);

    // Debounced search
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            buildSuggestions(value);
        }, 300);
    };

    // Close suggestions on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        setShowSuggestions(false);
        if (searchTerm.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setShowSuggestions(false);
        setSearchTerm('');
        navigate(suggestion.link);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveSuggestion(prev => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveSuggestion(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter' && activeSuggestion >= 0) {
            e.preventDefault();
            handleSuggestionClick(suggestions[activeSuggestion]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    return (
        <Navbar bg="white" expand="md" className="border-bottom shadow-sm sticky-top">
            <Container>
                <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
                    <img src="/dein-Imker.svg" alt="dein-Imker.de Logo" height="40" width="40" />
                    <span className="ms-2 fw-bold text-amber">dein-Imker.de</span>
                </Navbar.Brand>

                <Navbar.Toggle aria-controls="navbar-nav" />

                <Navbar.Collapse id="navbar-nav">
                    <Form onSubmit={handleSearch} className="d-flex mx-auto mt-2 mt-md-0 position-relative flex-grow-1" style={{ maxWidth: '600px' }} role="search" aria-label="Imker suchen">
                        <FormControl
                            ref={inputRef}
                            type="search"
                            placeholder={t('search.placeholder')}
                            className="rounded-pill border-1 ps-3 pe-5"
                            value={searchTerm}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                            autoComplete="off"
                        />
                        <Button
                            variant="amber"
                            className="rounded-circle position-absolute end-0 d-flex align-items-center justify-content-center"
                            style={{ top: '2px', right: '2px', width: '38px', height: '38px' }}
                            type="submit"
                        >
                            <FontAwesomeIcon icon={faSearch} size="sm" />
                        </Button>

                        {/* Autocomplete Dropdown */}
                        {showSuggestions && (
                            <div
                                ref={suggestionsRef}
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    background: '#fff',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    zIndex: 1050,
                                    marginTop: '4px',
                                    overflow: 'hidden'
                                }}
                            >
                                {suggestions.map((s, i) => (
                                    <div
                                        key={i}
                                        onClick={() => handleSuggestionClick(s)}
                                        onMouseEnter={() => setActiveSuggestion(i)}
                                        style={{
                                            padding: '10px 16px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            background: activeSuggestion === i ? '#f7f7f7' : '#fff',
                                            borderBottom: i < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                            transition: 'background 0.1s'
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={s.type === 'city' ? faMapMarkerAlt : faSearch}
                                            style={{ color: '#aaa', fontSize: '13px', width: '16px' }}
                                        />
                                        <div>
                                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#222' }}>{s.label}</div>
                                            <div style={{ fontSize: '12px', color: '#999' }}>{s.sublabel}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form>

                    <Nav className="ms-auto">
                        <Nav.Link as={Link} to="/for-beekeepers" className="d-none d-md-block text-secondary">
                            {t('common.forBeekeepers')}
                        </Nav.Link>
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};

export default Header;
